import {
  DepositoryType,
  InvestmentType,
  useAllUnitsQuery,
  useCreateDepositoryAccountMutation,
  useCreateInvestmentAccountMutation,
} from '@transactions/graphql';
import {ISO_4217_CODES} from 'common/isoCurrencies';
import * as React from 'react';
import {addAccount} from 'src/apolloCache/account';
import {Button} from 'src/Button';
import InputGroup from 'src/InputGroup';
import {TextInput} from 'src/inputs/TextInput';
import {LogoSelect} from 'src/LogoSelect';
import {Modal} from 'src/Modal';
import {useModals} from 'src/Modals';
import {SegmentedButton} from 'src/SegmentedButton';
import type {ApolloCache, FetchResult} from '@apollo/client';
import type {
  CreateDepositoryAccountMutation,
  CreateInvestmentAccountMutation,
} from '@transactions/graphql';
import type {SystemProps} from 'src/Modals';

export type AccountRole = 'depository' | 'investment';

export type CreateAccountModalUserProps = {
  onCreate?: () => void;
  // When set, the modal is locked to this role and the role selector is
  // hidden (e.g. the investments view only creates investment accounts).
  role?: AccountRole;
};

export const CreateAccountModal = (
  props: CreateAccountModalUserProps & SystemProps,
) => {
  const formId = React.useId();
  const lockedRole = props.role != null;
  const [role, setRole] = React.useState<AccountRole>(
    props.role ?? 'depository',
  );
  const [createDepositoryAccount, {loading: depositoryLoading}] =
    useCreateDepositoryAccountMutation();
  const [createInvestmentAccount, {loading: investmentLoading}] =
    useCreateInvestmentAccountMutation();
  const loading = depositoryLoading || investmentLoading;
  const [name, setName] = React.useState('');
  const [depositoryType, setDepositoryType] = React.useState<DepositoryType>(
    DepositoryType.Checking,
  );
  const [investmentType, setInvestmentType] = React.useState<InvestmentType>(
    InvestmentType.Brokerage,
  );
  const [logo, setLogo] = React.useState('');
  const [trackBalance, setTrackBalance] = React.useState(false);
  // Currency the account is denominated in. Non-USD makes the account a
  // wallet: raws are recorded in this currency and valued in USD through
  // conversion transfers. Two shapes: a 3-letter ISO code (USD, JPY) or a
  // custom unit picked by id — the id is what gets stored as the currency.
  const [currencyMode, setCurrencyMode] = React.useState<'code' | 'unit'>(
    'code',
  );
  const [currencyCode, setCurrencyCode] = React.useState('USD');
  const [unitId, setUnitId] = React.useState('');
  const {data: unitsData} = useAllUnitsQuery();
  const units = unitsData?.allUnits ?? [];
  const {createUnitModal} = useModals();
  // Membership, not just shape — the server enforces real ISO 4217 codes
  // (same vendored list, so browser and server can never disagree), so
  // catch the typo ("YEN") at the field instead of at submit.
  const codeValid = ISO_4217_CODES.has(currencyCode);
  const currency = currencyMode === 'code' ? currencyCode : unitId;
  const currencyValid = currencyMode === 'code' ? codeValid : unitId !== '';
  // The mutation's own loading flag drops as soon as createAccount resolves,
  // which would re-enable submit mid-chain and allow duplicate creates.
  const [submitting, setSubmitting] = React.useState(false);

  const handleDepositoryTypeChange = (
    e: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    const {value} = e.target;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    setDepositoryType(value as DepositoryType);
  };

  const handleInvestmentTypeChange = (
    e: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    const {value} = e.target;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    setInvestmentType(value as InvestmentType);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) {
      return;
    }
    setSubmitting(true);
    const create =
      role === 'investment'
        ? createInvestmentAccount({
            variables: {name, investmentType, logo: logo || null},
            update: (
              store: ApolloCache<object>,
              result: FetchResult<CreateInvestmentAccountMutation>,
            ) => {
              addAccount(store, result?.data?.createInvestmentAccount);
            },
          }).then((result) => result.data?.createInvestmentAccount._id)
        : createDepositoryAccount({
            variables: {
              name,
              depositoryType,
              logo: logo || null,
              trackBalance,
              currency,
            },
            update: (
              store: ApolloCache<object>,
              result: FetchResult<CreateDepositoryAccountMutation>,
            ) => {
              addAccount(store, result?.data?.createDepositoryAccount);
            },
          }).then((result) => result.data?.createDepositoryAccount._id);
    create
      .then(() => {
        props.onCreate?.();
        props.onRequestClose();
      })
      .finally(() => {
        setSubmitting(false);
      });
  };
  const footer = (
    <>
      <Button
        variant="outline"
        disabled={loading || submitting}
        onClick={props.onRequestClose}
      >
        Cancel
      </Button>
      <Button
        variant="primary"
        disabled={
          loading ||
          submitting ||
          name.length === 0 ||
          (role === 'depository' && !currencyValid)
        }
        type="submit"
        form={formId}
      >
        Create Account
      </Button>
    </>
  );
  return (
    <Modal
      onRequestClose={props.onRequestClose}
      header={
        lockedRole && role === 'investment'
          ? 'Create investment account'
          : 'Create account'
      }
      footer={footer}
    >
      <form id={formId} onSubmit={handleSubmit} className="flex flex-col gap-4">
        {!lockedRole && (
          <SegmentedButton
            options={[
              {value: 'depository', label: 'Depository'},
              {value: 'investment', label: 'Investment'},
            ]}
            value={role}
            onChange={setRole}
          />
        )}
        <InputGroup label="Name">
          <TextInput value={name} onChange={setName} autoFocus />
        </InputGroup>
        {role === 'depository' ? (
          <InputGroup label="Account type">
            <select value={depositoryType} onChange={handleDepositoryTypeChange}>
              <option value={DepositoryType.Checking}>Checking</option>
              <option value={DepositoryType.Savings}>Savings</option>
              <option value={DepositoryType.CreditCard}>Credit Card</option>
              <option value={DepositoryType.Cash}>Cash</option>
              <option value={DepositoryType.Wallet}>Wallet</option>
              <option value={DepositoryType.Loan}>Loan</option>
              <option value={DepositoryType.Ledger}>Ledger</option>
              <option value={DepositoryType.Other}>Other</option>
            </select>
          </InputGroup>
        ) : (
          <InputGroup label="Account type">
            <select value={investmentType} onChange={handleInvestmentTypeChange}>
              <option value={InvestmentType.Brokerage}>Brokerage</option>
              <option value={InvestmentType['401K']}>401(k)</option>
              <option value={InvestmentType.Ira}>IRA</option>
              <option value={InvestmentType.RothIra}>Roth IRA</option>
              <option value={InvestmentType.Hsa}>HSA</option>
              <option value={InvestmentType.Education}>529 plan</option>
              <option value={InvestmentType.Other}>Other</option>
            </select>
          </InputGroup>
        )}
        {role === 'depository' && (
          <InputGroup label="Currency">
            <div className="flex flex-col gap-2">
              <SegmentedButton
                options={[
                  {value: 'code', label: 'Currency code'},
                  {value: 'unit', label: 'Custom unit'},
                ]}
                value={currencyMode}
                onChange={setCurrencyMode}
              />
              {currencyMode === 'code' ? (
                <div className="flex flex-col gap-1">
                  <TextInput
                    value={currencyCode}
                    onChange={(value) => setCurrencyCode(value.toUpperCase())}
                    placeholder="USD"
                  />
                  <span
                    className={`text-xs ${
                      codeValid ? 'text-muted-foreground' : 'text-destructive'
                    }`}
                  >
                    {codeValid
                      ? 'Non-USD makes this a wallet: record amounts in this currency, link them to USD with conversion transfers.'
                      : 'Use an ISO currency code (e.g. USD, JPY). For passes, miles, and other non-monetary units, use a custom unit.'}
                  </span>
                </div>
              ) : (
                <div className="flex flex-col gap-1">
                  <div className="flex gap-2">
                    <select
                      value={unitId}
                      onChange={(e) => setUnitId(e.target.value)}
                      className="flex-1"
                    >
                      <option value="">Select a unit…</option>
                      {units.map((unit) => (
                        <option key={unit._id} value={unit._id}>
                          {unit.name}
                        </option>
                      ))}
                    </select>
                    <Button
                      variant="outline"
                      type="button"
                      onClick={() =>
                        createUnitModal.openModal({
                          onSuccess: (unit) => setUnitId(unit._id),
                        })
                      }
                    >
                      New unit
                    </Button>
                  </div>
                  <span className="text-muted-foreground text-xs">
                    For non-monetary wallets — passes, miles, credits. Amounts
                    render with the unit&apos;s own labels ("3 passes"), and
                    what they cost is tracked through conversion transfers.
                  </span>
                </div>
              )}
            </div>
          </InputGroup>
        )}
        {role === 'depository' && (
          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={trackBalance}
              onChange={(e) => setTrackBalance(e.target.checked)}
            />
            <span>
              Track nonzero balance
              <span className="text-muted-foreground block text-xs">
                Shows on the inbox while the balance isn&apos;t zero — gift
                cards, credits, loans.
              </span>
            </span>
          </label>
        )}
        <LogoSelect value={logo} onChange={setLogo} />
      </form>
    </Modal>
  );
};
